import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

import { AnalyticsService, LoggerService } from '@campfire-nx/api';

import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';
import { ProductDetailsService } from '@open-portal-app/core/services/product-details.service';
import { UserDataService } from '@open-portal-app/core/services/user-data.service';
import { PaymentService } from './payment.service';
import { AddressPipe } from '@open-portal-app/core/pipes/address.pipe';

import { UPDATE_PAYMENT_FLOW_STEP_ONE, UPDATE_PAYMENT_FLOW_STEP_TWO } from '../constants/update-payment-flow-v001';
import {
  POLICY_DETAILS_REVIEW_MARKUP,
  SOSURE_PREAMBLE_BACS_MARKUP,
  SOSURE_PREAMBLE_NONE_MARKUP,
} from '../constants/payment-markup';
import {
  PAYMENT_PRODUCT_ICONS,
  PAYMENT_HERO_ICONS
} from '../constants/payment.constants';

@Injectable({
  providedIn: 'root'
})
export class UpdatePaymentFlowManagerService {
  steps = [] as any;
  currentStepIndex = 0;
  policy!: any;
  policySlug!: string;
  productDetails!: any;
  locale!: { [key: string]: any };
  formattedPolicy!: { [key: string]: any };
  coveredEntityLabel!: string;
  productClass!: string;
  paymentMethod!: { [key: string]: any } | null;
  error!: any;

  constructor(
    public router: Router,
    private policyDetailsService: PolicyDetailsService,
    private productDetailsService: ProductDetailsService,
    private userDataService: UserDataService,
    private analyticsService: AnalyticsService,
    private loggerService: LoggerService,
    private location: Location,
    private paymentService: PaymentService,
    private addressPipe: AddressPipe
  ) { }

  getFirstStep(policy: { [key: string]: any }): Promise<any> {
    this.currentStepIndex = 0;
    this.policy = policy;
    this.productClass = policy.product.class;

    return new Promise((resolve, reject) => {
      this.paymentMethod = policy.payment_method;
      this.locale = this.policy.product?.country;
      this.productDetailsService.get(this.policy.product?.slug)
        .then((response: any) => {
          this.productDetails = response;
          return this.policyDetailsService.formatPolicy(this.policy)
        .then((response: any) => {
          this.formattedPolicy = response;
          this.policySlug = this.formattedPolicy.slug;
          // compile all the steps for the raptor flow
          this.steps = [this.createFirstStep(), this.createSecondStep()];
          this.analyticsService.trackEvent('Initialise', 'updatePaymentFlow', 'start-flow', this.steps[0].analytics);
          resolve(this.steps[0]);
        })
        .catch((error) => {
          if (this.steps[0] && this.steps[0].analytics) {
            this.analyticsService.trackEvent('Initialise', 'updatePaymentFlow', 'error', this.steps[0].analytics, { error });
          }
          this.loggerService.error(
            'UpdatePaymentFlowManager',
            'getFirstStep',
            { error: error }
          );

          reject(error);
        });
      });
    });
  };

  next(payload: any) {
    return new Promise((resolve) => {
      if (this.currentStepIndex === this.steps.length - 1) {
        this.router.navigate(['/dashboard']);
      };
      if (payload.payment_method?.token) {
        this.updatePolicy(payload.payment_method.token)
          .then((response: any) => {
            this.createLastStep(response.payment_method, resolve);
          });
      };
    });
  };

  back() {
    if (this.currentStepIndex === 0) {
      this.location.back();
    }
  }

  createFirstStep(): any {
    const firstStep = JSON.parse(JSON.stringify(UPDATE_PAYMENT_FLOW_STEP_ONE));
    const paymentReviewMarkup = this.paymentService.getPaymentReviewMarkup(this.paymentMethod);
    const policyDetailsMarkup = this.getPolicyDetailsReviewMarkup(this.policy, this.formattedPolicy, this.productClass);

    if (!this.paymentMethod) {
      firstStep.sections = firstStep.sections.filter((section: any) => {
        return section.uuid !== 'payment_review_section';
      });
      firstStep.info = {
        type: 'html',
        settings: {
          align: 'top'
        },
        content: SOSURE_PREAMBLE_NONE_MARKUP.replace('{{customer}}', this.formattedPolicy.userName).replace(/\s+/g, ' ').trim()
      };

      // add in the info box for the page if bacs or not paymentType
    } else if (this.paymentMethod.type === 'bacs_debit') {
      firstStep.info = {
        type: 'html',
        settings: {
          align: 'top'
        },
        content: SOSURE_PREAMBLE_BACS_MARKUP.replace('{{customer}}', this.formattedPolicy.userName).replace(/\s+/g, ' ').trim()
      };
    }

    // Build the first step
    firstStep.sections.forEach((section: any) => {
      // Setup the Policy Details section
      if (section.uuid === 'policy_details_section') {
        section.elements[0].info.content = policyDetailsMarkup.replace(/\s+/g, ' ').trim();
        section.settings.hero = PAYMENT_PRODUCT_ICONS[this.productClass];
      };

      // Setup the Payment Review section
      if (section.uuid === 'payment_review_section') {
        section.elements[0].info.content = paymentReviewMarkup.replace(/\s+/g, ' ').trim();
        section.settings.hero = PAYMENT_HERO_ICONS[this.paymentMethod?.type];
      };

      if (this.paymentMethod?.type === 'direct_debit') {
        firstStep.settings.submit_label = 'Set up direct debit';

        if (this.productDetails.slug === this.policy.product.slug) {
          // cover & premium are placeholder objects, so raptor form works withOUT issues
          firstStep.covers = [{
            cover: {},
            premium: {
              "U": {},
              "M": {}
            },
            pds: this.productDetails.pds
          }];
        }
      }

      // Setup the Update Payment section
      if (section.uuid === 'update_payment_section') {
        let selectedPayment: any;

        if (!this.paymentMethod || this.paymentMethod.type === 'bacs_debit') {
          selectedPayment = this.productDetails.payment_accounts?.find((account: any) => account.type === 'credit_card');
        } else {
          selectedPayment = this.productDetails.payment_accounts?.find((account: any) => account.type === this.paymentMethod?.type);
        }

        if (selectedPayment) {
          selectedPayment.params.user_email = this.policy.user.email;
          selectedPayment.params.user_slug = this.policy.user_slug;

          section.elements.forEach((element: any) => {
            if (element.key === 'payment_method') {
              element.settings.payment_options = [selectedPayment];
              element.settings.locale = this.locale;
            }
            if (element.key === 'confirmed_card_permission') {
              if (this.locale?.country_alpha2 === 'GB') {
                element.settings.text = element.settings.text.replace('{{partner}}', 'Open');
              } else {
                element.settings.text = element.settings.text.replace('{{partner}}', this.policy.partner.name);
              }
            }
          });
        }
      };
    });

    // Apply the data and human values to the firstStep
    firstStep.data.payment_type = this.paymentMethod?.type;
    firstStep.human.payment_type = this.paymentMethod?.type;
    firstStep.data.product_slug = this.productDetails.slug;
    firstStep.data.frequency = this.formattedPolicy.paymentFrequency.key;
    firstStep.human.product_slug = this.productDetails.slug;
    this.paymentService.addAnalytics(firstStep, this.policy, this.paymentMethod);

    return firstStep;
  }

  createSecondStep(): any {
    const secondStep = JSON.parse(JSON.stringify(UPDATE_PAYMENT_FLOW_STEP_TWO));
    const policyDetailsMarkup = this.getPolicyDetailsReviewMarkup(this.policy, this.formattedPolicy, this.productClass);
    // Build the second step
    secondStep.sections.forEach((section: any) => {
      // Setup the Policy Details section
      if (section.uuid === 'policy_details_success_section') {
        section.elements[0].info.content = policyDetailsMarkup.replace(/\s+/g, ' ').trim();
        section.settings.hero = PAYMENT_PRODUCT_ICONS[this.productClass];
      };
    });
    this.paymentService.addAnalytics(secondStep, this.policy, this.paymentMethod);

    return secondStep;
  }

  createLastStep(paymentMethod: any, resolve: (value: unknown) => void) {
    const lastStep = JSON.parse(JSON.stringify(this.steps[this.steps.length - 1]));
    let outstandingPaymentMarkup = '';
    const hasOverdueInvoices = this.formattedPolicy.overdueInvoices && this.formattedPolicy.overdueInvoices.invoices.length > 0;
    if (hasOverdueInvoices) {
      outstandingPaymentMarkup = this.paymentService.getOutstandingPaymentMarkup(
        this.formattedPolicy.overdueInvoices?.invoices,
        this.policy.product?.country?.currency_symbol,
        this.policy.slug || this.policy.uuid,
        this.formattedPolicy.isRaptorial,
        'update'
      );
    } else {
      // If no outstanding invoices remove payment_outstanding_info section
      lastStep.sections.splice(0, 1);
    }

    const policyDetailsMarkup = this.getPolicyDetailsReviewMarkup(this.policy, this.formattedPolicy, this.productClass);

    lastStep.sections.forEach((section: any) => {
      // Setup the Policy Details section
      if (hasOverdueInvoices && section.uuid === 'payment_outstanding_info') {
        section.elements[0].info.content = outstandingPaymentMarkup.replace(/\s+/g, ' ').trim();
      }
      if (section.uuid === 'policy_details_success_section') {
        section.elements[0].info.content = policyDetailsMarkup.replace(/\s+/g, ' ').trim();
        section.settings.hero = PAYMENT_PRODUCT_ICONS[this.productClass];
      }
      if (section.uuid === 'payment_review_success_section') {
        const updatedPaymentReviewMarkup = this.paymentService.getPaymentSuccessMarkup(paymentMethod);
        section.elements[0].info.content = updatedPaymentReviewMarkup.replace(/\s+/g, ' ').trim();
        section.settings.hero = PAYMENT_HERO_ICONS[paymentMethod?.type];
      }
    });

    this.steps[this.steps.length - 1] = lastStep;
    this.currentStepIndex++;
    this.analyticsService.trackEvent(
      'updatePaymentFlow',
      'updatePaymentDetails',
      'success',
      this.steps[this.currentStepIndex]?.analytics
    );

    resolve(this.steps[this.currentStepIndex]);
  }

  updatePolicy(token: any) {
    return new Promise((resolve, reject) => {
      const updatedPayload = {
        slug: this.policySlug,
        user_slug: this.policy.user_slug,
        payment_token: token
      };

      this.policyDetailsService.updatePolicyDetails(
        updatedPayload,
        this.productDetails.is_raptorial,
        this.productClass
      )
      .then((response: any) => {
        if (this.productDetails.is_raptorial) {
          resolve(this.getUpdatedPolicyData());
        } else {
          resolve(response);
        }
      });
    });
  }

  getUpdatedPolicyData() {
    return new Promise((resolve, reject) => {
      this.userDataService.getLatestUserData()
        .then((response) => {
          let matchingPolicy = response?.policies?.raptor?.find((policy: any) => policy.uuid === this.policy.uuid);

          resolve(matchingPolicy);
        })
        .catch((error) => {
          this.loggerService.error(
            'UpdatePaymentFlowManager',
            'getUpdatedPolicyData',
            { error: error }
          );

          reject(error);
        });
    });
  };

  getPolicyDetailsReviewMarkup(policy: any, formattedPolicy: any, productClass: string): string {
    let policyDetailsMarkup = POLICY_DETAILS_REVIEW_MARKUP;

    policyDetailsMarkup = policyDetailsMarkup.replace('{{coveredLabel}}', formattedPolicy.policyTitle);
    policyDetailsMarkup = policyDetailsMarkup.replace('{{policyTypeLabel}}', formattedPolicy.policyTypeLabel);
    policyDetailsMarkup = policyDetailsMarkup.replace('{{policyNumber}}', formattedPolicy.policyNumber);
    return policyDetailsMarkup;
  }
}
